import { useGetAuctionForms } from "apollo/hooks/queries";
import {
  AUCTION_STATUS_NOT_STARTED,
  QUERY_PROGRESS_SUCCEED,
  RECARE_FILE_TYPE,
  SEARCH_ACTION_GENERATE_FORM,
} from "core/consts";
import { getSearchActions } from "core/model/auctions";
import {
  Auction,
  AuctionForm,
  Patient,
  SearchAction,
  TransitionalCare,
} from "core/types";
import { margin } from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_24,
  FONT_WEIGHT_BOLD,
  Title,
} from "ds_legacy/materials/typography";
import { useOnEnter } from "dsl/atoms/Routes";
import PatientNavigation from "dsl/organisms/PatientNavigation";
import { useTranslations } from "translations";
import { BackToFiles } from "../RehabForms";
import { TransitionalCareFormDownload } from "./DownloadSection";
import { TransitionalCareForm } from "./Form";
import { FormExplanationHeader } from "./Header";
import { FormNonSubscribed } from "./NonSubscribers";
import { FormSearchNotStarted } from "./SearchNotStarted";

export const getTransitionalCareAction = (
  auction: Auction,
): SearchAction | null => {
  const { [SEARCH_ACTION_GENERATE_FORM]: generateFormAction } =
    getSearchActions(auction);

  if (!generateFormAction) {
    return null;
  }

  const availableFormTypes = generateFormAction.context?.values ?? [];
  const isTransitionalCareAvailable = availableFormTypes.includes(
    RECARE_FILE_TYPE.TRANSITIONAL_CARE,
  );

  if (!isTransitionalCareAvailable) {
    return null;
  }

  return generateFormAction;
};

export const findTransitionalCareForm = (
  forms: AuctionForm[] | undefined,
): AuctionForm | undefined => {
  return forms?.find(
    (form) => form.form_type === RECARE_FILE_TYPE.TRANSITIONAL_CARE,
  );
};

const ExplanationParagraph = ({
  auctionStatus,
}: {
  auctionStatus: Auction["status"];
}) => {
  const translations = useTranslations();

  if (auctionStatus === AUCTION_STATUS_NOT_STARTED) return null;

  return (
    <>
      <Body
        as="p"
        data-testid="started-banner"
        margin={margin(0, 0, 1)}
        maxWidth="100%"
      >
        {
          translations.patientForms.transitionalCareForm
            .explanationTransitionalCare
        }
      </Body>
      <Body as="p" margin={margin(0, 0, 3)} maxWidth="100%">
        {translations.patientForms.transitionalCareForm.numberOfProviders}
      </Body>
    </>
  );
};

export const TransitionalCarePresenter = ({
  auction,
  patient,
  transitionalCare,
}: {
  auction: Auction;
  patient: Patient;
  transitionalCare: Readonly<TransitionalCare | AuctionForm> | undefined;
}) => {
  const translations = useTranslations();
  const transitionalCareAction = getTransitionalCareAction(auction);

  if (!transitionalCareAction) {
    return null;
  }

  if (transitionalCareAction.context?.disabled) {
    return <FormNonSubscribed auction={auction} patient={patient} />;
  }

  const children =
    auction.status === AUCTION_STATUS_NOT_STARTED ? (
      <FormSearchNotStarted />
    ) : (
      <>
        <FormExplanationHeader transitionalCare={transitionalCare} />
        <TransitionalCareForm
          auction={auction}
          transitionalCare={transitionalCare}
        />
      </>
    );

  return (
    <div className="mx-auto my-[40px] w-full max-w-[1366px] overflow-visible rounded-md px-[120px] py-8 shadow-md">
      <BackToFiles auction={auction} patient={patient} />
      <Title
        as="h2"
        margin={margin(0, 0, 3)}
        style={{ fontSize: FONT_SIZE_24, fontWeight: FONT_WEIGHT_BOLD }}
      >
        {translations.patientForms.transitionalCareForm.title}
      </Title>
      <ExplanationParagraph auctionStatus={auction.status} />
      {transitionalCare && (
        <TransitionalCareFormDownload
          careseekerName={auction.patient.careseeker?.name ?? ""}
          externalId={auction.patient.external_id ?? ""}
          transitionalCare={transitionalCare}
          userId={auction.patient.user_id ?? ""}
        />
      )}
      {children}
    </div>
  );
};

export default function PatientTransitionalCare({
  auction,
  patient,
}: {
  auction: Auction;
  patient: Patient;
}) {
  useOnEnter();

  const [queryProgress, forms] = useGetAuctionForms({
    auctionId: auction.id,
  });

  const transitionalCare = findTransitionalCareForm(forms);

  if (queryProgress !== QUERY_PROGRESS_SUCCEED) return null;

  return (
    <PatientNavigation auction={auction} patient={patient}>
      <TransitionalCarePresenter
        auction={auction}
        patient={patient}
        transitionalCare={transitionalCare}
      />
    </PatientNavigation>
  );
}
